import React, { useContext, useEffect, useCallback, useState } from "react";
import NavBar from "../partials/NavBar";
import "react-toastify/dist/ReactToastify.css";
import StringResult from "../partials/stringResult";
import ItemResult from "../partials/itemResult";
import SkillResult from "../partials/skillResult";
import EnchantedItemResult from "../partials/enchantedItemResult";
import { GlobalContext } from "../GlobalContext";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import NpcResult from "../partials/npcResult";
import NameResult from "../partials/nameResult";

const Home = () => {
  const {
    isAuthenticated,
    setIsAuthenticated,
    isSingleLanguage,
    handleToggleChange,
    username,
    setUsername,
    password,
    setPassword,
    langMap,
    socket,
    isSocketReady,
    currentRequestIdRef,
    toast,
    logout,
    versions,
    updateDatabase,
    updateToastId,
    isDev,
    setToken,
    token,
  } = useContext(GlobalContext);

  const [searchType, setSearchType] = useState("SysString");
  const [searchVer, setSearchVer] = useState("");
  const [searchTypeChanging, setSearchTypeChanging] = useState(false);
  const [language, setLanguage] = useState("English");
  const [resultLang, setResultLang] = useState("Korean");
  const [searchTerm, setSearchTerm] = useState("");
  const [useRawOutput, setUseRawOutput] = useState(false);
  const [results, setResults] = useState([]);
  const [actualSearchTerm, setActualSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Once the server sends its supported versions, auto-select the newest
  // (highest id) if nothing is selected yet.
  useEffect(() => {
    if (searchVer || versions.length === 0) return;
    const newest = versions.reduce((a, b) => (b.id > a.id ? b : a));
    setSearchVer(String(newest.id));
  }, [versions, searchVer]);

  const login = async () => {
    try {
      const apiBase =
        process.env.REACT_APP_API_URL ||
        (isDev ? "http://localhost:5000" : "https://insc.vtrialonis.com");
      const apiUrl = `${apiBase}/api/login`;
      const response = await axios.post(apiUrl, {
        username,
        password,
      });
      setToken(response.data);
      localStorage.setItem("auth-token", response.data);
      localStorage.setItem("username", username);
      setIsAuthenticated(true);
      toast.success("Hello, " + username + "!", {
        position: "top-center",
        autoClose: 2000,
      });
    } catch (error) {
      if (error.response) {
        console.error("Error logging in", error.response.data);
        toast.error(error.response.data, {
          position: "top-center",
          autoClose: 2000,
        });
      }
      toast.error("Invalid username or password", {
        position: "top-center",
        autoClose: 2000,
      });
    }
  };

  const copyTerm = (src, term) => {
    navigator.clipboard.writeText(term);

    // NPC results aren't saved as terms — just copy.
    if (searchType === "npc" || !socket) {
      toast("Copied: " + term);
      return;
    }

    socket.emit("copyTerm", { src, term, language, resultLang }, (resp) => {
      if (resp && resp.ok) toast.success("Saved: " + term);
      else toast.error("Failed to save term");
    });
  };

  const onTermPaste = (e) => {
    var value = e.clipboardData.getData("Text");
    e.preventDefault();
    if (value.includes("[") && value.includes("]")) {
      value = value.substring(value.indexOf("[") + 1, value.indexOf("]"));
      setSearchTerm(value);
      return;
    } else if (value.includes("=")) {
      value = value.substring(value.indexOf("=") + 1);
      setSearchTerm(value);
      return;
    } else {
      setSearchTerm(value);
      return;
    }
  };

  const handleSearch = () => {
    // If the search button is clicked while a search is in progress, cancel the current search
    if (loading) {
      if (currentRequestIdRef.current && socket) {
        socket.emit("cancelSearch", {
          requestId: currentRequestIdRef.current,
        });
      }
      setLoading(false); // the button's class is derived from `loading` state
      return;
    }

    startNewSearch();
  };

  const startNewSearch = useCallback(() => {
    if (!socket || !searchTerm.trim()) return;

    const newRequestId = uuidv4();
    currentRequestIdRef.current = newRequestId;

    setResults([]);
    setSuggestions([]);
    setLoading(true); // the button's class is derived from `loading` state
    setActualSearchTerm(searchTerm);

    socket.emit("startItemSearch", {
      searchVer: searchVer,
      searchType,
      searchStr: searchTerm,
      inLang: langMap[language],
      outLang: langMap[resultLang],
      requestId: newRequestId,
      isSingleLanguage: isSingleLanguage,
      useRawOutput: useRawOutput,
    });
  }, [
    searchVer,
    searchTerm,
    searchType,
    language,
    resultLang,
    isSingleLanguage,
    langMap,
    useRawOutput,
  ]);

  useEffect(() => {
    if (!isSocketReady || !searchType || !searchVer || !searchTerm.trim()) return;

    const timer = setTimeout(() => {
      startNewSearch();
    }, 300); // Add debounce to avoid frequent triggers

    return () => clearTimeout(timer); // Cleanup timer
  }, [searchVer, searchType, searchTerm, isSocketReady, startNewSearch]);

  const onLanguageChange = () => {
    if (language === resultLang) {
      for (const lang in langMap) {
        if (lang !== language) {
          setResultLang(lang);
          break;
        }
      }
    }
  };

  const onResultLanguageChange = () => {
    if (language === resultLang) {
      for (const lang in langMap) {
        if (lang !== resultLang) {
          setLanguage(lang);
          break;
        }
      }
    }
  };

  useEffect(() => {
    onLanguageChange();
  }, [language]);

  useEffect(() => {
    onResultLanguageChange();
  }, [resultLang]);

  useEffect(() => {
    if (token) {
      setIsAuthenticated(true);
      if (!socket || !isSocketReady) {
        return;
      }

      // debug all socket events
      const logAnyEvent = (event, ...args) => {
        console.log(`Socket event received: ${event}`, args);
      };
      socket.onAny(logAnyEvent);

      socket.on("itemFound", (item) => {
        if (item.requestId === currentRequestIdRef.current) {
          // Iterate item.suggestions
          /*if (item.suggestions) {
            for (const [key, value] of Object.entries(item.suggestions)) {
              setSuggestions((prevSuggestions) => {
                let found = false;
            
                // Update count if key-value pair exists
                const updatedSuggestions = prevSuggestions.map((suggestion) => {
                  if (suggestion.src === key && suggestion.dest === value) {
                    found = true;
                    return { ...suggestion, count: suggestion.count + 1 };
                  }
                  return suggestion;
                });
            
                // If key-value pair not found, add new suggestion
                if (!found) {
                  return [...updatedSuggestions, { key, value, count: 1 }];
                }
            
                return updatedSuggestions;
              });
            }
          }*/
          if (item.suggestions) {
            item.suggestions.forEach((match) => {
              setSuggestions((prevSuggestions) => {
                let found = false;

                const updatedSuggestions = prevSuggestions.map((suggestion) => {
                  if (
                    suggestion.key === match.src &&
                    suggestion.value === match.dest
                  ) {
                    found = true;
                    return { ...suggestion, count: suggestion.count + 1 };
                  }
                  return suggestion;
                });

                if (!found) {
                  return [
                    ...updatedSuggestions,
                    { key: match.src, value: match.dest, count: 1 },
                  ];
                }

                return updatedSuggestions;
              });
            });
          }
          setResults((prevResults) => [...prevResults, item]); // Append new item to the list
        }
      });

      socket.on("searchCanceled", (data) => {
        if (data.requestId === currentRequestIdRef.current) {
          setLoading(false);
          console.log(`Search with requestId ${data.requestId} was canceled.`);
        }
      });

      // Listen for the end of results
      socket.on("endOfResults", () => {
        setLoading(false);
        setSearchTypeChanging(false);
      });

      socket.on("status", (message) => {
        toast(message, { position: "bottom-center" });
      });

      // Handle error messages
      socket.on("error", (message) => {
        toast.error(message, { position: "bottom-center" });
        setLoading(false);
        setSearchTypeChanging(false);
      });

      socket.on("updateMessage", (message, type) => {
        if (updateToastId.current === null) {
          updateToastId.current = toast(message, {
            position: "top-center",
            autoClose: false,
            closeButton: false,
            type: type,
          });
        }
        switch (type) {
          case "success":
            toast.update(updateToastId.current, {
              render: message,
              type: "success",
              autoClose: 5000,
              className: "rotateY animated",
            });
            break;
          case "error":
            toast.update(updateToastId.current, {
              render: message,
              type: "error",
              autoClose: 5000,
              className: "rotateY animated",
            });
            break;
          case "information":
            toast.update(updateToastId.current, {
              render: message,
              type: "info",
              autoClose: false,
              className: "rotateY animated",
              closeButton: false,
            });
            break;
          default:
            toast(message, { position: "top-right", autoClose: false });
        }
      });

      socket.on("message", (message, type) => {
        switch (type) {
          case "success":
            toast.success(message, {
              autoClose: 5000,
              className: "rotateY animated",
              position: "bottom-center",
            });
            break;
          case "error":
            toast.error(message, {
              autoClose: 5000,
              className: "rotateY animated",
              position: "bottom-center",
            });
            break;
          case "information":
            toast.info(message, {
              autoClose: 5000,
              className: "rotateY animated",
              position: "bottom-center",
            });
            break;
          default:
            toast(message, { position: "bottom-center", autoClose: false });
        }
      });

      // Subscribe to server logs for admin users
      const lowerUsername = username.toLowerCase();
      if (lowerUsername === 'lyrei' || lowerUsername === 'admin') {
        socket.emit('subscribeLogs');
        
        socket.on('serverLog', ({ timestamp, level, message }) => {
          setServerLogs((prevLogs) => [
            ...prevLogs,
            { timestamp, level, message, id: Date.now() + Math.random() }
          ].slice(-500)); // Keep last 500 logs
        });
      }

      return () => {
        socket.offAny(logAnyEvent);
        socket.off("itemFound");
        socket.off("endOfResults");
        socket.off("error");
        socket.off("status");
        socket.off("searchCanceled");
        socket.off("updateMessage");
        socket.off("message");

        // Unsubscribe from server logs for admin users
        const lowerUsername = username.toLowerCase();
        if (lowerUsername === 'lyrei' || lowerUsername === 'admin') {
          socket.emit('unsubscribeLogs');
          socket.off('serverLog');
        }
      };
    } else {
      // Clear logs when not authenticated
      setServerLogs([]);
    }
  }, [socket, isSocketReady, setResults, token, username]);

  const [isOverlayVisible, setOverlayVisible] = useState(false);
  const [showServerConsole, setShowServerConsole] = useState(false);
  const [serverLogs, setServerLogs] = useState([]);

  const isAdminUser = username.toLowerCase() === 'lyrei' || username.toLowerCase() === 'admin';

  const toggleOverlay = (state) => {
    setOverlayVisible(state);
  };

  const clearServerLogs = () => {
    setServerLogs([]);
  };

  return (
    <div className="App">
      <h1>L2 Localization Tool</h1>
      {isOverlayVisible && (
          <div className="overlay">
            <div className="overlay-content">
              <h2>Update Database</h2>
              <p>Are you sure you want to update the database?</p>
              <div className="modal-buttons">
                <button onClick={() => toggleOverlay(false)}>Cancel</button>
                {versions.map((v) => (
                  <button key={v.id} onClick={() => updateDatabase(v.id, true)}>
                    Update {v.id}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      {!isAuthenticated ? (
        <div className="auth-container">
          <h2>Login</h2>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Username"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
          />
          <button onClick={login}>Login</button>
        </div>
      ) : (
        <div className="search-container">
          <NavBar
            username={username}
            onLogout={logout}
            updateDatabase={updateDatabase}
            toggleOverlay={toggleOverlay}
          />
          <h2>Search</h2>
          <div className="top-container">
            <div className="search-field-container">
              <div className="toggle-and-language-container">
                <div className="toggle-section">
                  <label className="toggle-label">Single Language</label>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={isSingleLanguage}
                      onChange={handleToggleChange}
                    />
                    <span className="sliderBtn round"></span>
                  </label>
                </div>
                <div className="language-section">
                  <div className="form-group column">
                    <label className="form-label-small">Search Language</label>
                    <select
                      className="form-select"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                    >
                      {Object.keys(langMap).map((lang) => (
                        <option key={lang} value={lang}>
                          {lang}
                        </option>
                      ))}
                    </select>
                  </div>

                  {!isSingleLanguage && (
                    <>
                      <button
                        className="swap-button"
                        onClick={() => {
                          const temp = language;
                          setLanguage(resultLang);
                          setResultLang(temp);
                        }}
                        title="Swap languages"
                        style={{
                          margin: "0 10px",
                          padding: "5px 10px",
                          backgroundColor: "#4a4a4a",
                          border: "none",
                          borderRadius: "4px",
                          color: "white",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: "30px",
                          height: "30px",
                        }}
                      >
                        ⇄
                      </button>
                      <div className="form-group column">
                        <label className="form-label-small">
                          Result Language
                        </label>
                        <select
                          className="form-select"
                          value={resultLang}
                          onChange={(e) => setResultLang(e.target.value)}
                        >
                          {Object.keys(langMap).map((lang) => (
                            <option key={lang} value={lang}>
                              {lang}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="search-type-container">
                <div className="form-group">
                  <div className="form-group column">
                    <label className="form-label-small">Search Type</label>
                    <select
                      className="form-select"
                      value={searchType}
                      onChange={(e) => setSearchType(e.target.value)}
                    >
                      <option value="SysString">SysString</option>
                      <option value="skill">Skill</option>
                      <option value="item">Item</option>
                      <option value="enchanteditem">
                        Enchanted Item Skills
                      </option>
                      <option value="npc">NPC</option>
                      <option value="npcstring">NPCString</option>
                      <option value="huntingzone">HuntingZone</option>
                      <option value="names">Items, skills, NPCs - Names only</option>
                    </select>
                  </div>
                  <div className="form-group column">
                    <label className="form-label-small">Version</label>
                    <select
                      className="form-select"
                      value={searchVer}
                      onChange={(e) => setSearchVer(e.target.value)}
                    >
                      {versions.map((v) => (
                        <option key={v.id} value={String(v.id)}>
                          {v.label || v.id}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <div className="form-group column">
                  <label className="form-label-small">Raw Output</label>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={useRawOutput}
                      onChange={() => setUseRawOutput(!useRawOutput)}
                    />
                    <span className="sliderBtn round small"></span>
                  </label>
                  </div>
                  <div className="form-group column">
                  <label className="form-label-small">Search Term</label>
                  <input
                    className="form-input"
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onPaste={(e) => onTermPaste(e)}
                    placeholder="Enter item name"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearch();
                    }}
                  />
                </div>
                <div className="form-group column" style={{ alignSelf: "end" }}>
                  <div className="search-button-section">
                    <button
                      className={
                        loading ? "search-button searching" : "search-button"
                      }
                      onClick={handleSearch}
                      disabled={searchTerm === ""}
                    >
                      {loading ? "Cancel" : "Search"}
                    </button>
                  </div>
                </div>
                </div>
              </div>
            </div>
            {suggestions.length > 0 && (
              <div className="suggestions-container">
                <center>
                  <h3>Suggestions</h3>
                </center>
                <table className="suggestions-table">
                  <thead>
                    <tr>
                      <th>{language}</th>
                      <th>{resultLang}</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggestions
                      .slice()
                      .sort((a, b) => b.count - a.count) // Sort by count in descending order
                      .slice(0, 10) // Display only the top 10 suggestions
                      .map((suggestion, index) => (
                        <tr key={index} className="suggestion">
                          <td>{suggestion.key}</td>
                          <td
                            onClick={() =>
                              copyTerm(suggestion.key, suggestion.value)
                            }
                          >
                            {suggestion.value}
                          </td>
                          <td>{suggestion.count}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {results.length > 0 && (
            <div className="result-header">
              <h2>
                {results.length} results for "{actualSearchTerm}"
              </h2>
              <span
                className="loader"
                style={loading ? { opacity: "1" } : undefined}
              ></span>
            </div>
          )}
          {results.length === 0 &&
            searchTerm !== "" &&
            !loading &&
            !searchTypeChanging && (
              <div className="result-header center">
                <h2>No results found for "{actualSearchTerm}"</h2>
              </div>
            )}
          {results.length > 0 && (
            <ul className="results-list">
              {results.map((item, index) => {
                switch (searchType.toLowerCase()) {
                  case "sysstring":
                  case "huntingzone":
                  case "npcstring":
                    return (
                      <StringResult
                        item={item}
                        key={index}
                        term={searchTerm}
                        useRaw={useRawOutput}
                      />
                    );
                  case "item":
                    return (
                      <ItemResult
                        item={item}
                        key={index}
                        term={searchTerm}
                        useRaw={useRawOutput}
                      />
                    );
                  case "skill":
                    return (
                      <SkillResult
                        item={item}
                        key={index}
                        term={searchTerm}
                        useRaw={useRawOutput}
                      />
                    );
                  case "npc":
                    return (
                      <NpcResult item={item} key={index} term={searchTerm} />
                    );
                  case "names":
                    return (
                      <NameResult item={item} key={index} term={searchTerm} />
                    );
                  case "enchanteditem":
                    return (
                      <EnchantedItemResult
                        item={item}
                        key={index}
                        term={searchTerm}
                        useRaw={useRawOutput}
                      />
                    );
                  default:
                    return null;
                }
              })}
            </ul>
          )}
        </div>
      )}

      {/* Server Console Toggle and Panel for Admin Users */}
      {isAuthenticated && isAdminUser && (
        <>
          <button
            className="server-console-toggle"
            onClick={() => setShowServerConsole(!showServerConsole)}
            title={showServerConsole ? "Hide Server Console" : "Show Server Console"}
          >
            {showServerConsole ? "▼ Hide Console" : "▲ Show Console"}
            {serverLogs.length > 0 && !showServerConsole && (
              <span className="log-badge">{serverLogs.length}</span>
            )}
          </button>

          {showServerConsole && (
            <div className="server-console">
              <div className="server-console-header">
                <span>Server Logs</span>
                <div className="server-console-actions">
                  <button onClick={clearServerLogs} className="console-clear-btn">
                    Clear
                  </button>
                  <button
                    onClick={() => setShowServerConsole(false)}
                    className="console-close-btn"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="server-console-body">
                {serverLogs.length === 0 ? (
                  <div className="console-empty">No logs yet...</div>
                ) : (
                  serverLogs.map((log) => (
                    <div
                      key={log.id}
                      className={`console-log-entry console-log-${log.level.toLowerCase()}`}
                    >
                      <span className="console-timestamp">[{log.timestamp}]</span>
                      <span className={`console-level console-level-${log.level.toLowerCase()}`}>
                        [{log.level}]
                      </span>
                      <span className="console-message">{log.message}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Home;
